<script>
import Spinner from 'shared/components/Spinner.vue';
import Cookies from 'js-cookie';

export default {
  components: {
    Spinner,
  },
  data() {
    return {
      error: null,
      processing: true,
      statusMessage: '',
      closeText: '',
      errorTitle: '',
    };
  },
  mounted() {
    this.statusMessage = this.$t('CALENDAR.OAUTH_CALLBACK.CONNECTING');
    this.closeText = this.$t('CALENDAR.OAUTH_CALLBACK.CLOSE');
    this.errorTitle = this.$t('CALENDAR.OAUTH_CALLBACK.ERROR_TITLE');
    this.handleCallback();
  },
  methods: {
    getAuthHeaders() {
      const savedAuthInfo = Cookies.get('cw_d_session_info');
      if (savedAuthInfo) {
        try {
          const authData = JSON.parse(savedAuthInfo || '{}');
          return {
            'access-token': authData['access-token'],
            'token-type': authData['token-type'],
            client: authData.client,
            expiry: authData.expiry,
            uid: authData.uid,
          };
        } catch {
          return {};
        }
      }
      return {};
    },
    async handleCallback() {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code') || this.$route?.query?.code;
      const state = urlParams.get('state') || this.$route?.query?.state;
      const errorParam = urlParams.get('error');

      // Check for OAuth error from Google
      if (errorParam) {
        this.error = `Google OAuth Error: ${errorParam}`;
        this.processing = false;
        this.tryClosePopup();
        return;
      }

      if (!code || !state) {
        this.error = this.$t('CALENDAR.ERRORS.AUTH_CODE_NOT_FOUND');
        this.processing = false;
        this.tryClosePopup();
        return;
      }

      let accountId;
      try {
        const decodedState = atob(state);
        [accountId] = decodedState.split(':');
      } catch {
        this.error = this.$t('CALENDAR.ERRORS.INVALID_STATE');
        this.processing = false;
        this.tryClosePopup();
        return;
      }

      if (!accountId) {
        this.error = this.$t('CALENDAR.ERRORS.ACCOUNT_ID_NOT_FOUND');
        this.processing = false;
        this.tryClosePopup();
        return;
      }

      this.statusMessage = this.$t('CALENDAR.OAUTH_CALLBACK.EXCHANGING_TOKENS');

      try {
        const authHeaders = this.getAuthHeaders();

        const response = await fetch(
          `/api/v1/accounts/${accountId}/google_calendar/callback`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...authHeaders,
            },
            credentials: 'include',
            body: JSON.stringify({ code }),
          }
        );

        const data = await response.json();

        if (data.success) {
          this.statusMessage = this.$t('CALENDAR.OAUTH_CALLBACK.SUCCESS');
          this.notifyParentAndClose(data.email, accountId);
        } else {
          this.error = data.error || this.$t('CALENDAR.ERRORS.CONNECT_FAILED');
          this.processing = false;
        }
      } catch (err) {
        this.error = err.message || this.$t('CALENDAR.ERRORS.CALLBACK_ERROR');
        this.processing = false;
      }
    },
    notifyParentAndClose(email, accountId) {
      // Try to notify opener window
      if (window.opener && !window.opener.closed) {
        try {
          window.opener.postMessage(
            { type: 'google-calendar-connected', email },
            window.location.origin
          );
          // Give time for the message to be received
          setTimeout(() => {
            window.close();
          }, 500);
          return;
        } catch {
          // If postMessage fails, fall through to redirect
        }
      }

      // If we can't close, redirect to settings
      window.location.href = `/app/accounts/${accountId}/settings/integrations/google-calendar`;
    },
    tryClosePopup() {
      if (window.opener && !window.opener.closed) {
        setTimeout(() => {
          window.close();
        }, 3000);
      }
    },
    closeWindow() {
      if (window.opener && !window.opener.closed) {
        window.close();
      } else {
        // Get accountId from state if available
        const urlParams = new URLSearchParams(window.location.search);
        const state = urlParams.get('state');
        if (state) {
          try {
            const decodedState = atob(state);
            const [accountId] = decodedState.split(':');
            if (accountId) {
              window.location.href = `/app/accounts/${accountId}/settings/integrations/google-calendar`;
              return;
            }
          } catch {
            // ignore
          }
        }
        window.location.href = '/app';
      }
    },
  },
};
</script>

<template>
  <div
    class="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-900"
  >
    <div class="text-center p-8 max-w-md">
      <template v-if="processing">
        <Spinner size="large" />
        <p class="mt-4 text-slate-600 dark:text-slate-400">
          {{ statusMessage }}
        </p>
      </template>
      <template v-else-if="error">
        <div class="text-red-600 dark:text-red-400 mb-4">
          <svg
            class="w-12 h-12 mx-auto mb-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <p class="font-medium">{{ errorTitle }}</p>
          <p class="text-sm mt-2">{{ error }}</p>
        </div>
        <button
          class="px-4 py-2 bg-woot-500 text-white rounded hover:bg-woot-600 transition-colors"
          @click="closeWindow"
        >
          {{ closeText }}
        </button>
      </template>
    </div>
  </div>
</template>
